import {
  Alert,
  Box,
  Button,
  Card,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Skeleton,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ReferralUsersResponse } from "../types";
import { fetchUserReferrals } from "../utils/referrals-api";

const formatDate = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

export function ReferralDetailsSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { referralId } = useParams();
  const numericId = Number(referralId);

  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  // Drilling into another user resets pagination.
  useEffect(() => {
    setPage(1);
  }, [referralId]);

  const { data, isLoading, isError, error } = useQuery<
    ReferralUsersResponse,
    Error
  >({
    queryKey: ["user-referrals", numericId, page, limit],
    queryFn: () => fetchUserReferrals(numericId, { page, limit }),
    enabled: Number.isFinite(numericId),
  });

  const parent = data?.parent;
  const items = data?.items ?? [];
  const totalPages = data?.meta?.totalPages ?? 1;
  const totalItems = data?.meta?.totalItems;

  return (
    <Card sx={{ p: isMobile ? 2 : 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Direct Referrals
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Number.isFinite(numericId)
              ? `User ID: ${numericId}`
              : "Invalid user ID"}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate("/referrals")}>
          Back to Referrals
        </Button>
      </Box>

      {isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      ) : null}

      {/* Referrer of this user */}
      <Paper sx={{ p: 2, mb: 3 }} elevation={0} variant="outlined">
        <Typography variant="h6" mb={2}>
          Referrer
        </Typography>
        {isLoading ? (
          <Skeleton variant="text" height={32} />
        ) : parent ? (
          <Stack direction={isMobile ? "column" : "row"} spacing={2}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                User
              </Typography>
              <Typography>{parent.username || `User ${parent.id}`}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                ID
              </Typography>
              <Typography>{parent.id}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Telegram ID
              </Typography>
              <Typography>{parent.telegramId || "—"}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Joined At
              </Typography>
              <Typography>{formatDate(parent.createdAt)}</Typography>
            </Box>
            <Box display="flex" alignItems="flex-end">
              <Button
                size="small"
                variant="text"
                onClick={() => navigate(`/referrals/${parent.id}`)}
              >
                View their referrals
              </Button>
            </Box>
          </Stack>
        ) : (
          <Typography color="text.secondary">
            This user was not referred by anyone.
          </Typography>
        )}
      </Paper>

      {/* Direct referrals table */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6">Referred Users</Typography>
        {totalItems !== undefined ? (
          <Typography variant="body2" color="text.secondary">
            {totalItems} total
          </Typography>
        ) : null}
      </Box>

      <Paper elevation={0} sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        {isLoading ? (
          <Box p={2}>
            {[...Array(4)].map((_, index) => (
              <Box key={index} display="flex" gap={2} py={2} px={2}>
                <Skeleton variant="text" width="15%" height={30} />
                <Skeleton variant="text" width="25%" height={30} />
                <Skeleton variant="text" width="25%" height={30} />
                <Skeleton variant="text" width="20%" height={30} />
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "action.hover" }}>
                  <TableCell>ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Telegram ID</TableCell>
                  <TableCell>Joined At</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      This user has no direct referrals.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {user.username || `User ${user.id}`}
                        </Typography>
                      </TableCell>
                      <TableCell>{user.telegramId || "—"}</TableCell>
                      <TableCell>{formatDate(user.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Referrals">
                          <IconButton
                            onClick={() => navigate(`/referrals/${user.id}`)}
                            size="small"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(event, value) => setPage(value)}
          color="primary"
          size={isMobile ? "small" : "large"}
        />
      </Box>
    </Card>
  );
}
